import {
  DecisionKey,
  FeatureFlagClientImpl,
  FeatureFlagClientNullImpl,
  ToolkitAccountID,
  createFeatureDecisions,
  isDecisionKey,
} from '@aloft/toolkit-shared';
import { createInstance } from '@optimizely/optimizely-sdk';

import { useEffect, useState } from 'react';

import { logger } from '../logging/logging.js';
import { loadFeatures } from '../redux/features/featuresSlice.js';
import { useAppDispatch } from '../redux/hooks.js';
import { getConfig } from '../utils/getConfig.js';
import { whatEnv } from '../utils/whatEnv.js';

const log = logger();

const optimizelyClient = createInstance({
  sdkKey: getConfig('OPTIMIZELY_SDK_KEY'),
  logLevel: 'ERROR',
  datafileOptions: {
    autoUpdate: true,
    urlTemplate: getConfig('OPTIMIZELY_DATAFILE_URL'),
  },
});

// keep track of userID used for feature flag bucketing
let globalUserID: ToolkitAccountID | undefined;

// only allow setting globalUserID once per session
// allowing this value to change in the course of a session is not allowed by optimizely
// if it becomes necessary to feature flag pre-login features, we will need to add a user service to
// optimizely, which would allow us to use a stable userID between authed and unauthed states
export function setGlobalUserID(userID: ToolkitAccountID) {
  if (isGlobalUserIDSet()) return;
  globalUserID = userID;
}
function isGlobalUserIDSet() {
  return globalUserID !== undefined;
}

// DO NOT EXPORT -- featureDecisions is the singleton we expose to app logic
const _featureFlagClientInternal = optimizelyClient
  ? new FeatureFlagClientImpl(optimizelyClient, () => globalUserID, log, whatEnv())
  : new FeatureFlagClientNullImpl(log);

const featureDecisions = Object.freeze(createFeatureDecisions(_featureFlagClientInternal));

export const useFeatureClientReadyState = () => {
  const [isReady, setIsReady] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (optimizelyClient) {
      optimizelyClient.onReady().then(() => {
        setIsReady(true);
        const features: { [k in DecisionKey]: boolean } = {} as {
          [k in DecisionKey]: boolean;
        };
        for (const key in featureDecisions) {
          if (isDecisionKey(key)) {
            features[key] = featureDecisions[key]();
          }
        }
        dispatch(loadFeatures(features));
      });
    } else {
      setIsReady(true);
    }
  }, [dispatch]);
  return isReady;
};
